import React, { useState, useMemo, useEffect } from 'react';
import Header from '../../components/ui/Header';
import QuickActionToolbar from '../../components/ui/QuickActionToolbar';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import ExpenseFilters from './components/ExpenseFilters';
import ExpenseGroupedTable from './components/ExpenseGroupedTable';
import ExpenseModal from './components/ExpenseModal';
import BulkActionsBar from './components/BulkActionsBar';
import ExpenseStats from './components/ExpenseStats';
import { getExpenses, createExpense, updateExpense, approveExpense, rejectExpense } from '../../services/expenseService';
import { getChartOfAccounts } from '../../services/accountService';
import { useCurrentCompany } from '../../hooks/useCompany';


const Expenses = () => {
  const [expenses, setExpenses] = useState([]);
  const [accounts, setAccounts] = useState([]);

  // Get company data for currency
  const { data: company } = useCurrentCompany();

  const [filters, setFilters] = useState({
    search: '',
    fromDate: '',
    toDate: '',
    category: '',
    vendor: '',
    paymentMethod: 'all',
    minAmount: ''
  });

  const [selectedExpenses, setSelectedExpenses] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [sortConfig, setSortConfig] = useState({ field: 'date', direction: 'desc' });

  // Dynamic vendors from API
  const [vendors, setVendors] = useState([{ value: 'all', label: 'All Vendors' }]);

  // Convert expense accounts to category options
  const expenseCategories = useMemo(() => {
    // Filter accounts to only include Expense type accounts
    const expenseAccounts = accounts.filter(account => 
      account.type === 'Expense' || account.accountType === 'Expense' || account.AccountType === 'Expense'
    );
    
    const categoryOptions = expenseAccounts.map(account => ({
      value: account.name || account.accountName || account.AccountName,
      label: `${account.code || account.accountCode || account.AccountCode} - ${account.name || account.accountName || account.AccountName}`,
      accountId: account.id || account.Id
    }));
    
    console.log('Expense accounts for categories:', expenseAccounts);
    console.log('Category options:', categoryOptions);
    
    // Add "All Categories" option at the beginning
    return [
      { value: 'all', label: 'All Categories' },
      ...categoryOptions
    ];
  }, [accounts]);


  // Convert asset accounts to payment method options
  const paymentMethods = useMemo(() => {
    // Filter accounts to only include Asset type accounts
    const assetAccounts = accounts.filter(account => 
      account.type === 'Asset' || account.accountType === 'Asset' || account.AccountType === 'Asset'
    );
    
    const accountOptions = assetAccounts.map(account => ({
      value: account.name || account.accountName || account.AccountName,
      label: `${account.code || account.accountCode || account.AccountCode} - ${account.name || account.accountName || account.AccountName}`,
      accountId: account.id || account.Id
    }));
    
    console.log('Asset accounts for payment methods:', assetAccounts);
    console.log('Payment method options:', accountOptions);
    
    return accountOptions;
  }, [accounts]);

  useEffect(() => {
    loadExpenses();
    loadAccounts();
  }, []);

  const loadAccounts = async () => {
    try {
      const accountsData = await getChartOfAccounts();
      console.log('All accounts from Chart of Accounts:', accountsData);
      
      // Store ALL accounts - we'll filter them in the useMemo hooks for specific purposes
      setAccounts(accountsData);
    } catch (err) {
      console.error('Error loading accounts:', err);
      setAccounts([]);
    }
  };

  const loadCategoriesAndVendors = async () => {
    try {
      // Get vendors from existing expense data
      const expensesData = await getExpenses();
      const uniqueVendors = [...new Set(expensesData.map(expense => expense.vendor).filter(Boolean))];
      
      const vendorOptions = [
        { value: 'all', label: 'All Vendors' },
        ...uniqueVendors.map(vendor => ({
          value: vendor,
          label: vendor
        }))
      ];
      
      setVendors(vendorOptions);
      
      console.log('Loaded vendors from expenses:', vendorOptions);
    } catch (err) {
      console.error('Error loading vendors:', err);
      // Keep default values if API fails
    }
  };

  const loadExpenses = async () => {
    try {
      const data = await getExpenses();
      setExpenses(data);
    } catch (err) {
      console.error('Error loading expenses:', err);
    }
  };

  const filteredExpenses = useMemo(() => {
    return expenses?.filter((expense) => {
      const searchTerm = filters?.search?.toLowerCase() || '';
      const matchesSearch = !searchTerm || 
        expense?.vendor?.toLowerCase()?.includes(searchTerm) ||
        expense?.description?.toLowerCase()?.includes(searchTerm) ||
        expense?.category?.toLowerCase()?.includes(searchTerm) ||
        expense?.referenceNumber?.toLowerCase()?.includes(searchTerm);
      
      const matchesCategory = filters?.category === 'all' || 
        filters?.category === '' || 
        expense?.category?.toLowerCase()?.includes(filters?.category?.toLowerCase());
      
      const matchesVendor = filters?.vendor === 'all' || 
        filters?.vendor === '' || 
        expense?.vendor?.toLowerCase()?.includes(filters?.vendor?.toLowerCase());
      
      const matchesPayment = filters?.paymentMethod === 'all' || expense?.paymentMethod === filters?.paymentMethod;
      const matchesMinAmount = !filters?.minAmount || expense?.amount >= parseFloat(filters?.minAmount);

      const expenseDate = new Date(expense.date);
      const matchesFromDate = !filters?.fromDate || expenseDate >= new Date(filters.fromDate);
      const matchesToDate = !filters?.toDate || expenseDate <= new Date(filters.toDate);

      return matchesSearch && matchesCategory && matchesVendor && matchesPayment &&
      matchesMinAmount && matchesFromDate && matchesToDate;
    });
  }, [expenses, filters]);

  const groupedExpenses = useMemo(() => {
    const sorted = [...filteredExpenses];
    sorted?.sort((a, b) => {
      let aValue = a?.[sortConfig?.field];
      let bValue = b?.[sortConfig?.field];

      if (sortConfig?.field === 'date') {
        aValue = new Date(aValue);
        bValue = new Date(bValue);
      } else if (sortConfig?.field === 'amount') {
        aValue = parseFloat(aValue);
        bValue = parseFloat(bValue);
      } else if (typeof aValue === 'string') {
        aValue = aValue?.toLowerCase();
        bValue = bValue?.toLowerCase();
      }

      if (aValue < bValue) return sortConfig?.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig?.direction === 'asc' ? 1 : -1;
      return 0;
    });

    // Group expenses by category
    const grouped = sorted.reduce((acc, expense) => {
      const category = expense?.category || 'Uncategorized';
      if (!acc[category]) {
        acc[category] = [];
      }
      acc[category].push(expense);
      return acc;
    }, {});

    return grouped;
  }, [filteredExpenses, sortConfig]);

  const stats = useMemo(() => {
    const total = expenses?.reduce((sum, exp) => sum + exp?.amount, 0);
    const pending = expenses?.filter((exp) => exp?.status === 'pending')?.length;
    const pendingAmount = expenses?.filter((exp) => exp?.status === 'pending')?.reduce((sum, exp) => sum + exp?.amount, 0);
    const approved = expenses?.filter((exp) => exp?.status === 'approved')?.length;
    const approvedAmount = expenses?.filter((exp) => exp?.status === 'approved')?.reduce((sum, exp) => sum + exp?.amount, 0);
    const taxDeductible = expenses?.filter((exp) => exp?.taxDeductible)?.reduce((sum, exp) => sum + exp?.amount, 0);
    const taxDeductibleCount = expenses?.filter((exp) => exp?.taxDeductible)?.length;

    return {
      total,
      totalChange: 12.5,
      pending,
      pendingAmount,
      approved,
      approvedAmount,
      taxDeductible,
      taxDeductibleCount
    };
  }, [expenses]);

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
  };

  const handleClearFilters = () => {
    setFilters({
      search: '',
      fromDate: '',
      toDate: '',
      category: '',
      vendor: '',
      paymentMethod: 'all',
      minAmount: ''
    });
  };

  const handleSelectExpense = (id) => {
    setSelectedExpenses((prev) =>
    prev?.includes(id) ? prev?.filter((expId) => expId !== id) : [...prev, id]
    );
  };

  const handleSelectAll = () => {
    const allExpenses = Object.values(groupedExpenses).flat();
    if (selectedExpenses?.length === allExpenses?.length) {
      setSelectedExpenses([]);
    } else {
      setSelectedExpenses(allExpenses?.map((exp) => exp?.id));
    }
  };

  const handleSort = (field) => {
    setSortConfig((prev) => ({
      field,
      direction: prev?.field === field && prev?.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleAddExpense = () => {
    setEditingExpense(null);
    setIsModalOpen(true);
  };

  const handleEditExpense = (expense) => {
    setEditingExpense(expense);
    setIsModalOpen(true);
  };

  const handleSaveExpense = async (expenseData) => {
    try {
      if (editingExpense) {
        // Update existing expense
        const updatedExpense = await updateExpense(editingExpense.id, expenseData, accounts);
        setExpenses((prev) => prev?.map((exp) => exp?.id === expenseData?.id ? updatedExpense : exp));
      } else {
        // Create new expense - pass accounts array for account ID lookup
        const newExpense = await createExpense(expenseData, accounts);
        setExpenses((prev) => [...prev, newExpense]);
      }
      
      // Reload expenses and refresh vendors to get fresh data
      await loadExpenses();
      await loadCategoriesAndVendors();
    } catch (error) {
      console.error('Error saving expense:', error);
      // Show error message to user
      alert('Failed to save expense. Please try again.');
    }
  };

  const handleDeleteExpense = (id) => {
    if (window.confirm('Are you sure you want to delete this expense?')) {
      setExpenses((prev) => prev?.filter((exp) => exp?.id !== id));
      setSelectedExpenses((prev) => prev?.filter((expId) => expId !== id));
    }
  };

  const handleApproveExpense = async (expense) => {
    try {
      await approveExpense(expense.id);
      
      // Update the expense status in the local state
      setExpenses((prev) => prev?.map((exp) =>
        exp?.id === expense?.id ? { ...exp, status: 'approved' } : exp
      ));
      
      console.log('Expense approved successfully:', expense.id);
    } catch (error) {
      console.error('Error approving expense:', error);
      alert('Failed to approve expense. Please try again.');
    }
  };

  const handleRejectExpense = async (expense) => {
    if (window.confirm('Are you sure you want to reject this expense?')) {
      try {
        await rejectExpense(expense.id);
        
        // Update the expense status in the local state
        setExpenses((prev) => prev?.map((exp) =>
          exp?.id === expense?.id ? { ...exp, status: 'rejected' } : exp
        ));
        
        console.log('Expense rejected successfully:', expense.id);
      } catch (error) {
        console.error('Error rejecting expense:', error);
        alert('Failed to reject expense. Please try again.');
      }
    }
  };

  const handleBulkApprove = () => {
    setExpenses((prev) => prev?.map((exp) =>
    selectedExpenses?.includes(exp?.id) ? { ...exp, status: 'approved' } : exp
    ));
    setSelectedExpenses([]);
  };

  const handleBulkReject = () => {
    setExpenses((prev) => prev?.map((exp) =>
    selectedExpenses?.includes(exp?.id) ? { ...exp, status: 'rejected' } : exp
    ));
    setSelectedExpenses([]);
  };

  const handleBulkExport = () => {
    const selectedData = expenses?.filter((exp) => selectedExpenses?.includes(exp?.id));
    console.log('Exporting expenses:', selectedData);
    alert(`Exporting ${selectedData?.length} expenses to CSV`);
  };

  const handleBulkDelete = () => {
    if (window.confirm(`Are you sure you want to delete ${selectedExpenses?.length} expenses?`)) {
      setExpenses((prev) => prev?.filter((exp) => !selectedExpenses?.includes(exp?.id)));
      setSelectedExpenses([]);
    }
  };

  const handleBulkCategoryUpdate = () => {
    alert('Category update feature coming soon');
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-16">
        <div className="max-w-[1600px] mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-6 md:mb-8">
            <div>
              <h1 className="font-heading font-bold text-2xl md:text-3xl lg:text-4xl text-foreground mb-2">
                Expense Management
              </h1>
              <p className="font-body text-sm md:text-base text-muted-foreground">
                Track, categorize, and manage business expenses with automated workflows
              </p>
            </div>
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
              <Button
                variant="default"
                onClick={handleAddExpense}
                iconName="Plus"
                iconPosition="left"
                fullWidth
                className="sm:w-auto">

                Add Expense
              </Button>
            </div>
          </div>

          <ExpenseStats stats={stats} currency={company?.currency} />

          <ExpenseFilters
            filters={filters}
            onFilterChange={handleFilterChange}
            onClearFilters={handleClearFilters}
            categories={expenseCategories}
            vendors={vendors}
            paymentMethods={paymentMethods} />


          <div className="bg-card rounded-lg shadow-sm border border-border p-4 md:p-6 mb-4">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4">
              <div className="flex items-center gap-2">
                <Icon name="FileText" size={20} color="var(--color-primary)" />
                <h2 className="font-heading font-semibold text-lg md:text-xl text-foreground">
                  Expenses by Category
                </h2>
                <span className="caption text-muted-foreground">
                  ({Object.values(groupedExpenses).flat().length} {Object.values(groupedExpenses).flat().length === 1 ? 'expense' : 'expenses'})
                </span>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={handleBulkExport}
                iconName="Download"
                iconPosition="left">

                Export All
              </Button>
            </div>

            {Object.keys(groupedExpenses).length > 0 ?
            <ExpenseGroupedTable
              groupedExpenses={groupedExpenses}
              selectedExpenses={selectedExpenses}
              onSelectExpense={handleSelectExpense}
              onSelectAll={handleSelectAll}
              onEdit={handleEditExpense}
              onDelete={handleDeleteExpense}
              onApprove={handleApproveExpense}
              onReject={handleRejectExpense}
              onSort={handleSort}
              sortConfig={sortConfig} /> :


            <div className="text-center py-12 md:py-16">
                <div className="w-16 h-16 md:w-20 md:h-20 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                  <Icon name="Receipt" size={32} color="var(--color-muted-foreground)" />
                </div>
                <h3 className="font-heading font-semibold text-lg md:text-xl text-foreground mb-2">
                  No expenses found
                </h3>
                <p className="font-body text-sm md:text-base text-muted-foreground mb-6">
                  {filters?.search || filters?.category || filters?.vendor ?
                'Try adjusting your filters to see more results' : 'Get started by adding your first expense'}
                </p>
                <Button
                variant="default"
                onClick={handleAddExpense}
                iconName="Plus"
                iconPosition="left">

                  Add Expense
                </Button>
              </div>
            }
          </div>
        </div>
      </main>
      <ExpenseModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={handleSaveExpense}
        expense={editingExpense}
        categories={expenseCategories}
        vendors={vendors}
        paymentMethods={paymentMethods} />

      <BulkActionsBar
        selectedCount={selectedExpenses?.length}
        onApprove={handleBulkApprove}
        onReject={handleBulkReject}
        onExport={handleBulkExport}
        onDelete={handleBulkDelete}
        onCategoryUpdate={handleBulkCategoryUpdate}
        onClearSelection={() => setSelectedExpenses([])} />

      <QuickActionToolbar actions={[]} />
    </div>);

};

export default Expenses;